/* tslint-disable-no-implicit-any */
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { SetupSleeperData, SetupSleeperForm, SetupSleeperValues } from '@models/app/helpers.model';
import { PopupData } from '@models/app/modal-data.model';
import { Bed } from '@models/bed/bed.model';
import { Store } from '@ngxs/store';
import { BedSide } from '@shared/utils/helpers/enum.helper';
import { RegistrationStringResource } from '@shared/utils/helpers/registration-string.helper';
import { RegistrationHelper } from '@shared/utils/helpers/registration.helper';
import { SiqPopupHelper } from '@shared/utils/helpers/siq-popup.helper';
import { YearHelper } from '@shared/utils/helpers/year.helper';
import { CloseModal } from '@store/app/app.actions';
import { Logout } from '@store/auth/auth.actions';
import { SetSleeperDataProperty } from '@store/setup/setup.actions';
import { SetupSelectors } from '@store/setup/setup.selectors';
import { Subject, filter, takeUntil } from 'rxjs';

const DEFAULT_GENDER_VALUE = 9;

@Component({
  selector: 'app-setup-sleeper-form',
  templateUrl: './setup-sleeper-form.component.html'
})
export class SetupSleeperFormComponent implements OnInit, OnDestroy {
  @Input() isPartnerSetup: boolean;
  @Output() redirectToTheNextScreen = new EventEmitter<void>();
  controlFocusState: Record<keyof SetupSleeperForm, boolean> = {
    firstName: false,
    birthMonth: false,
    birthYear: false,
    height: false,
    weight: false,
    sleepGoal: false,
    gender: false,
    bedId: false,
    side: false,
    duration: false
  };
  dropdownDefaultValues: SetupSleeperValues;
  isFormSubmitted = false;
  selectedBed: Bed;
  form: FormGroup<SetupSleeperForm>;
  setupData = RegistrationStringResource.sleeperData();

  private _unsubscribeAll = new Subject<void>();

  constructor(private store: Store, private siqPopupHelper: SiqPopupHelper) { }

  ngOnInit(): void {
    const bedData = this.store.selectSnapshot(SetupSelectors.bedSetupData);
    this.store.select(SetupSelectors.selectedBed).pipe(
      takeUntil(this._unsubscribeAll),
      filter((bed): bed is Bed => !!bed)
    ).subscribe(bed => {
      this.selectedBed = bed;
      this.dropdownDefaultValues = RegistrationStringResource.sleeperDataDefaultValues(this.selectedBed.isKidsBed);
      const bedSide = this.isPartnerSetup ? (Number(!(bedData?.side)) ?? null) : (bedData?.side ?? null);
      const sleeperData = this.store.selectSnapshot(SetupSelectors.sleeperData);
      this.form = RegistrationHelper.createSetupSleeperForm(bed, bedSide, sleeperData);
    });
  }

  getQuestion(property: string): string {
    return RegistrationStringResource.sleeperSetupQuestions(this.isPartnerSetup)[property];
  }

  getDropdownValue(property: string): number {
    const value = this.getFormControlValue(property);
    return value !== null ? Number(value) : this.dropdownDefaultValues[property as keyof SetupSleeperValues];
  }

  hasErrors(field: string): boolean {
    if (!this.controlFocusState[field as keyof SetupSleeperForm]) {
      const control = this.form.controls[field as keyof SetupSleeperForm];
      return control.touched && !control.valid;
    }
    return false;
  }

  setControlFocusState(focusState: object): void {
    this.controlFocusState = focusState as Record<keyof SetupSleeperForm, boolean>;
    // set firstName value into state on focus out
    if (!this.controlFocusState['firstName']) {
      this.store.dispatch(new SetSleeperDataProperty('firstName', this.getFormControlValue('firstName') ?? ''));
    }
  }

  isInvalidField(field: string): boolean {
    if (!this.controlFocusState[field as keyof SetupSleeperForm]) {
      const control = this.form.controls[field as keyof SetupSleeperForm];
      if (control?.touched) {
        return this.form && !this.isFormSubmitted
          ? this.checkIsInputValid(control)
          : false;
      }
    }
    return false;
  }

  getValue(property: string): string {
    const value = property === 'birthYear' ? parseInt(this.form.controls.birthYear.value ?? '', 10) : this.form.get(property)?.value;
    const optionIndex = (this.setupData as Record<string, any>)[`${property}Values`].indexOf(value);
    return (this.setupData as Record<string, any>)[`${property}Options`][optionIndex];
  }

  handleDropdownSelect(selectedValue: number | string, property: string): void {
    // @ts-expect-error selectedValue won't be null
    this.form.controls[property as keyof SetupSleeperForm].setValue(selectedValue);
    // to convert year into appropriate type
    if (property === 'birthYear') selectedValue = selectedValue.toString();
    this.store.dispatch(new SetSleeperDataProperty(property, selectedValue));
  }

  next(): void {
    this.isFormSubmitted = true;
    const setupData = this.form.getRawValue() as SetupSleeperData;
    // set other sleeper properties
    this.setBedPropertiesIntoSleeperData(setupData);
    if (setupData.birthYear && YearHelper.checkKidYears(setupData.birthYear)) {
      this.openKidPrivacyPolicyPopup();
    } else {
      this.completeSleeperSetup();
    }
  }

  private setBedPropertiesIntoSleeperData(setupData: SetupSleeperData): void {
    // 1. case: user did not answer on gender question 
    if (setupData.gender === null) {
      setupData.gender = DEFAULT_GENDER_VALUE;
      this.store.dispatch(new SetSleeperDataProperty('gender', DEFAULT_GENDER_VALUE));
    }
    // 2. case: set bedId, side and duration
    const bedId = setupData.bedId;
    if (bedId)
      this.store.dispatch(new SetSleeperDataProperty('bedId', bedId));

    const side = setupData.side;
    if (side === BedSide.Left || side === BedSide.Right)
      this.store.dispatch(new SetSleeperDataProperty('side', side));

    const duration = setupData.duration;
    if (duration)
      this.store.dispatch(new SetSleeperDataProperty('duration', duration));
  }

  private completeSleeperSetup(): void {
    this.redirectToTheNextScreen.emit();
  }

  private getFormControlValue(control: string): string | number | null {
    return this.form.get(control)?.value;
  }

  private checkIsInputValid(item: FormControl): boolean {
    return !item?.valid;
  }

  private openKidPrivacyPolicyPopup(): void {
    const kidPrivacyPolicyCTA = RegistrationStringResource.kidPrivacyPolicyPopupData;
    const popupData = new PopupData({
      title: (kidPrivacyPolicyCTA as Record<string, string>)['title'],
      text: (kidPrivacyPolicyCTA as Record<string, string>)['text'],
      icon: 'warning-icon',
      type: 'yellow',
      screen: 'Setup Sleeper',
      rightBtnTxt: 'Acknowledge',
      leftBtnTxt: 'Decline'
    });
    const modal = this.siqPopupHelper.createPopup(popupData);
    modal.componentInstance.onClose.subscribe(() => {
      this.store.dispatch(new CloseModal(modal));
    });
    modal.componentInstance.onRightAction.subscribe(() => {
      this.store.dispatch(new CloseModal(modal));
      this.completeSleeperSetup();
    });
    modal.componentInstance.onLeftAction.subscribe(() => {
      this.store.dispatch(new CloseModal(modal));
      this.store.dispatch(new Logout());
    });
    modal.afterClosed().subscribe(() => {
      this.store.dispatch(new CloseModal(modal));
      modal.componentInstance.onRightAction.unsubscribe();
      modal.componentInstance.onLeftAction.unsubscribe();
      modal.componentInstance.onClose.unsubscribe();
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
